import { useEffect, useState } from 'react';
import { Ambulance, Car, CarTaxiFront, ShieldCheck } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Reveal } from '@/components/ui/Reveal';
import { AnimatedCounter } from '@/components/ui/AnimatedCounter';
import CPAMBadge from '@/components/ui/CPAMBadge';
import EquipementsModernesSection from '@/components/sections/EquipementsModernesSection';
import { useSEO } from '@/lib/seo';
import api from '@/lib/api';

const CATEGORIES = [
  { key: 'ambulances', label: 'Ambulances' },
  { key: 'vsl', label: 'VSL' },
  { key: 'taxis', label: 'Taxis' },
  { key: 'bariatrique', label: 'Bariatrique' },
  { key: 'equipes', label: 'Équipes' },
  { key: 'implantations', label: 'Implantations' },
  { key: 'materiel', label: 'Matériel' },
];

export default function Flotte() {
  useSEO({
    title: "Notre flotte | Groupe Ambulances Beaulon",
    description: "Découvrez notre flotte : 6 ambulances, 8 VSL, 12 taxis conventionnés et véhicules spécialisés pour vos transports sanitaires dans l'Allier.",
    keywords: "flotte ambulance, vsl allier, taxi conventionné allier, véhicules sanitaires"
  });

  const [stats, setStats] = useState({ ambulances: 6, vsl: 8, taxis: 13 });
  const [photos, setPhotos] = useState([]);
  const [activeTab, setActiveTab] = useState('ambulances');

  useEffect(() => {
    api.get('/stats').then(r => setStats(r.data)).catch(() => {});
    api.get('/gallery').then(r => setPhotos(r.data)).catch(() => {});
  }, []);

  const filteredPhotos = photos.filter(p => p.category === activeTab);

  return (
    <div>
      <section className="hero-wash">
        <div className="container-app pt-14 pb-10">
          <Reveal>
            <p className="badge-soft mb-3">Notre flotte</p>
            <h1 className="h1 text-anthracite-950">Une flotte moderne au service de votre confort</h1>
            <p className="lead mt-4 max-w-3xl">Des véhicules régulièrement entretenus, du matériel adapté et des équipes formées pour assurer sécurité et qualité à chaque trajet.</p>
          </Reveal>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-app">
          {/* Big counters */}
          <div className="grid sm:grid-cols-3 gap-4 mb-12">
            {[
              { icon: Ambulance, value: stats.ambulances, label: 'Ambulances' },
              { icon: Car, value: stats.vsl, label: 'VSL' },
              { icon: CarTaxiFront, value: stats.taxis, label: 'Taxis conventionnés' },
            ].map((s, i) => (
              <Reveal key={s.label} delay={i * 80}>
                <div className="card-base p-6 flex items-center gap-4" data-testid={`fleet-stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
                  <div className="h-14 w-14 rounded-2xl bg-brand-50 text-brand-700 flex items-center justify-center">
                    <s.icon className="h-7 w-7" />
                  </div>
                  <div>
                    <div className="text-4xl font-heading font-semibold text-anthracite-950">
                      <AnimatedCounter value={s.value} />
                    </div>
                    <div className="text-sm text-muted-foreground">{s.label}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Gallery tabs */}
          <Reveal>
            <div className="flex flex-col gap-6">
              <h2 className="h2 text-anthracite-950">Galerie de notre flotte</h2>
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" data-testid="fleet-gallery">
                <TabsList className="w-full overflow-x-auto justify-start bg-anthracite-100/60 p-1 rounded-xl">
                  {CATEGORIES.map(c => (
                    <TabsTrigger key={c.key} value={c.key} className="data-[state=active]:bg-white data-[state=active]:text-brand-700 data-[state=active]:shadow-sm" data-testid={`gallery-category-tab-${c.key}`}>
                      {c.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
                {CATEGORIES.map(c => (
                  <TabsContent key={c.key} value={c.key} className="mt-6">
                    {photos.filter(p => p.category === c.key).length === 0 ? (
                      <div className="py-16 text-center text-muted-foreground border border-dashed rounded-2xl">
                        Aucune photo pour le moment.
                      </div>
                    ) : (
                      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {photos.filter(p => p.category === c.key).map(p => (
                          <figure key={p.id} className="group relative aspect-[4/3] rounded-2xl overflow-hidden border bg-anthracite-100 shadow-card" data-testid="gallery-image-item">
                            <img src={p.url} alt={p.title || c.label} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                            {p.title && (
                              <figcaption className="absolute bottom-0 inset-x-0 p-3 bg-gradient-to-t from-black/70 to-transparent text-white text-sm">{p.title}</figcaption>
                            )}
                          </figure>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                ))}
              </Tabs>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Section Équipements modernes (HoverJack, bariatrique, etc.) */}
      <EquipementsModernesSection />
    </div>
  );
}
